import { createFileRoute, notFound } from "@tanstack/react-router";
import { MapboxMap } from "../../../components/map/mapbox-map";
import { Marker } from "react-map-gl/mapbox";
import { Home, Store } from "lucide-react";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { orderQueryOptions } from "@/lib/orders";
import z from "zod";
import { createClient } from "@/lib/supabase/client";
import { useEffect } from "react";
import { OrderStatusBanner } from "@/components/order-status-banner";
import OrderClock from "@/components/order-clock";
import { parseISO } from "date-fns";
import { websiteConfigQueryOptions } from "@/lib/website-config";
import { useCartStore } from "@/components/cart/store";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import I18NLink from "@/components/i18n-link";
import { OrderTypes } from "@/lib/supabase/enums";
import { OrderSummaryCard } from "@/components/order-summary-card";
import { StorefrontOrderStatus } from "@/components/storefront/storefront-order-status";
import { SalesChannels } from "@/lib/storefront/channel";

const orderSearchSchema = z.object({
  secret: z.string().optional(),
});

export const Route = createFileRoute("/$locale/order/$orderReference")({
  validateSearch: (search) => orderSearchSchema.parse(search),
  component: RouteComponent,
  beforeLoad: async ({ params, search, context }) => {
    // An order is only readable with its secret. Without one this is not an
    // order link at all (iOS asks for /nl/order/favicon.ico, for one).
    if (!search.secret) throw notFound();

    context.queryClient.ensureQueryData(websiteConfigQueryOptions());
    // Not awaited, so use prefetchQuery: it never rejects. A failed fetch
    // reaches the page through useSuspenseQuery instead of surfacing as an
    // unhandled rejection on the server.
    void context.queryClient.prefetchQuery(
      orderQueryOptions(params.orderReference, search.secret)
    );
  },
});

function RouteComponent() {
  const { orderReference } = Route.useParams();
  const { secret } = Route.useSearch();

  //global state
  const clearCart = useCartStore((state) => state.clearCart);

  //queries
  const order = useSuspenseQuery(orderQueryOptions(orderReference, secret));
  const { data: websiteConfig } = useSuspenseQuery(websiteConfigQueryOptions());

  //hooks
  const supabase = createClient();
  const queryClient = useQueryClient();
  const { t } = useTranslation("translation");

  useEffect(() => {
    if (order.data.status !== "failed" && order.data.status !== "pending_payment") {
      clearCart();
    }
  }, []);

  useEffect(() => {
    const orderChannel = supabase.channel(
      `order-update-channel-${orderReference}-${secret}`
    );
    orderChannel.on<{ orderStatus: string; confirmedTime?: string }>(
      "broadcast",
      { event: "order-update" },
      (payload) => {
        // A webshop order's tracking details come with a status change, not
        // in the broadcast: read the order again.
        if (order.data.channel === SalesChannels.storefront) {
          void queryClient.invalidateQueries({
            queryKey: orderQueryOptions(orderReference, secret).queryKey,
          });
          return;
        }
        queryClient.setQueryData(
          orderQueryOptions(orderReference, secret).queryKey,
          {
            ...order.data,
            status: payload.payload.orderStatus,
            confirmedTime: payload.payload.confirmedTime
              ? parseISO(payload.payload.confirmedTime)
              : null,
          }
        );
      }
    );
    orderChannel.subscribe();
    return () => {
      orderChannel.unsubscribe();
    };
  }, [orderReference, secret]);

  if (!order.data) {
    return <div>Order not found</div>;
  }

  if (order.data.status === "pending_payment") {
    return (
      <div className="flex flex-col relative h-full items-center justify-center">
        <div className="px-3 md:px-4 pb-6 md:pb-8 flex justify-center w-full">
          <div className="w-full max-w-2xl text-center flex flex-col items-center justify-center">
            <h2 className="text-2xl font-bold mb-4 text-foreground">
              {t("Order.pending_payment_title")}
            </h2>
            <p className="text-muted-foreground mb-8 leading-relaxed">
              {t("Order.pending_payment_message")}
            </p>
            <Button asChild>
              <I18NLink to="/checkout">{t("Order.go_to_checkout")}</I18NLink>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (order.data.status === "failed") {
    return (
      <div className="flex flex-col relative h-full items-center justify-center">
        <div className="px-3 md:px-4 pb-6 md:pb-8 flex justify-center w-full">
          <div className="w-full max-w-2xl text-center flex flex-col items-center justify-center">
            <h2 className="text-2xl font-bold mb-4 text-foreground">
              {t("Order.failed_title")}
            </h2>
            <p className="text-muted-foreground mb-8 leading-relaxed">
              {t("Order.failed_message")}
            </p>
            <Button asChild>
              <I18NLink to="/checkout">{t("Order.go_to_checkout")}</I18NLink>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (order.data.channel === SalesChannels.storefront) {
    return (
      <StorefrontOrderStatus order={order.data} reference={orderReference} />
    );
  }

  const restaurantLat = websiteConfig.companyAddress.lat;
  const restaurantLon = websiteConfig.companyAddress.lon;
  const deliveryLat = order.data.deliveryLatitude;
  const deliveryLon = order.data.deliveryLongitude;
  const isDelivery =
    order.data.orderType === OrderTypes.delivery &&
    deliveryLat != null &&
    deliveryLon != null;

  const mapInitialViewState =
    isDelivery && restaurantLat != null && restaurantLon != null
      ? {
          bounds: [
            [
              Math.min(restaurantLon, deliveryLon),
              Math.min(restaurantLat, deliveryLat),
            ],
            [
              Math.max(restaurantLon, deliveryLon),
              Math.max(restaurantLat, deliveryLat),
            ],
          ] as [[number, number], [number, number]],
          fitBoundsOptions: { padding: 80 },
        }
      : {
          latitude: restaurantLat,
          longitude: restaurantLon,
          zoom: 14,
        };

  return (
    <div className="flex flex-col relative">
      <MapboxMap initialViewState={mapInitialViewState} className="border-b">
        {restaurantLat != null && restaurantLon != null && (
          <Marker
            latitude={restaurantLat}
            longitude={restaurantLon}
            anchor="bottom"
          >
            <div
              className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-white text-white shadow-md"
              style={{ backgroundColor: websiteConfig.primaryColor }}
              aria-label={t("Order.restaurant_marker_label")}
            >
              <Store className="h-5 w-5" />
            </div>
          </Marker>
        )}
        {isDelivery && (
          <Marker
            latitude={deliveryLat}
            longitude={deliveryLon}
            anchor="bottom"
          >
            <div
              className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-white bg-foreground text-background shadow-md"
              aria-label={t("Order.delivery_address_marker_label")}
            >
              <Home className="h-5 w-5" />
            </div>
          </Marker>
        )}
      </MapboxMap>
      <div className="flex-1 p-4 -mt-14 items-center justify-center flex">
        <OrderClock confirmedTime={order.data.confirmedTime} />
      </div>
      <div className="px-3 md:px-4 pb-6 md:pb-8 flex justify-center">
        <div className="w-full max-w-2xl space-y-4">
          {order.data?.status && (
            <OrderStatusBanner
              status={order.data.status}
              orderType={order.data.orderType as any}
              confirmedTime={order.data.confirmedTime}
            />
          )}
          {order.data?.summary && order.data.summary.items.length > 0 && (
            <OrderSummaryCard summary={order.data.summary} />
          )}
        </div>
      </div>
    </div>
  );
}
